import { Path, useWatch } from 'react-hook-form';

import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { useFormContext } from '@/components/react-hook-form';
import { GiftGrowthRate } from '@/modules/content/tooltipContent/GiftGrowthRate';
import { GiftingProposalCashFlowCashFlowType } from '@/types/schema';

import { BaselineIncomeAndExpensesFormShape } from '../../GiftDesignerBasicInformationForm.types';
import { validateYearRange } from '../../GiftDesignerModelScenariosForm.utils';

type BaselineIncomeAndExpensesField = Path<BaselineIncomeAndExpensesFormShape>;

function Name() {
  const {
    control,
    formState: { isLoading },
  } = useFormContext<BaselineIncomeAndExpensesFormShape>();

  return (
    <FormAwareTextInput<BaselineIncomeAndExpensesFormShape>
      control={control}
      fieldName={
        'displayName' as const satisfies BaselineIncomeAndExpensesField
      }
      label="Name"
      disabled={isLoading}
      required
    />
  );
}

function CashFlowType() {
  const { control } = useFormContext<BaselineIncomeAndExpensesFormShape>();

  return (
    <FormAwareRadioGroup<BaselineIncomeAndExpensesFormShape>
      control={control}
      fieldName={
        'cashFlowType' as const satisfies BaselineIncomeAndExpensesField
      }
      options={[
        {
          label: 'Income',
          value: GiftingProposalCashFlowCashFlowType.Income,
        },
        {
          label: 'Expense',
          value: GiftingProposalCashFlowCashFlowType.Expense,
        },
      ]}
      row={true}
      required
    />
  );
}

function Amount() {
  const { control } = useFormContext<BaselineIncomeAndExpensesFormShape>();

  return (
    <FormAwareCurrencyInput<BaselineIncomeAndExpensesFormShape>
      control={control}
      fieldName={'amount' as const satisfies BaselineIncomeAndExpensesField}
      isDecimalJSInput
      decimalScale={2}
      label="After-tax amount"
      required
    />
  );
}

// Need to use a custom type here because the radio group form expects value to be a string
export enum AnnuallyRecurringValue {
  true = 'isAnnuallyRecurring',
  false = 'isOneTime',
}

function AnnuallyRecurring() {
  const { control } = useFormContext<BaselineIncomeAndExpensesFormShape>();

  return (
    <FormAwareRadioGroup<BaselineIncomeAndExpensesFormShape>
      control={control}
      fieldName={
        'annuallyRecurring' as const satisfies BaselineIncomeAndExpensesField
      }
      options={[
        {
          label: 'Annually recurring',
          value: AnnuallyRecurringValue.true,
        },
        {
          label: 'One-time',
          value: AnnuallyRecurringValue.false,
        },
      ]}
      row={true}
      required
    />
  );
}

function StartYear({ isAnnuallyRecurring }: { isAnnuallyRecurring: boolean }) {
  const { control } = useFormContext<BaselineIncomeAndExpensesFormShape>();

  const lengthOfAnalysis = useWatch({
    control,
    name: 'lengthOfAnalysis',
  });

  const endYear = useWatch({
    control,
    name: 'endYear',
  });

  return (
    <FormAwareFormattedNumberInput<BaselineIncomeAndExpensesFormShape>
      control={control}
      label={isAnnuallyRecurring ? 'Start year' : 'Year of income or expense'}
      fieldName={'startYear' as const satisfies BaselineIncomeAndExpensesField}
      fixedDecimalScale={true}
      decimalScale={0}
      isDecimalJSInput
      required
      validation={{
        minMax: (value) =>
          validateYearRange(value, lengthOfAnalysis, {
            lt: endYear,
          }),
      }}
    />
  );
}

function EndYear() {
  const { control } = useFormContext<BaselineIncomeAndExpensesFormShape>();

  const lengthOfAnalysis = useWatch({
    control,
    name: 'lengthOfAnalysis',
  });

  const startYear = useWatch({
    control,
    name: 'startYear',
  });

  return (
    <FormAwareFormattedNumberInput<BaselineIncomeAndExpensesFormShape>
      control={control}
      label="End year"
      fieldName={'endYear' as const satisfies BaselineIncomeAndExpensesField}
      fixedDecimalScale={true}
      decimalScale={0}
      isDecimalJSInput
      required
      validation={{
        minMax: (value) =>
          validateYearRange(value, lengthOfAnalysis, {
            gt: startYear,
          }),
      }}
    />
  );
}

function GrowthRate() {
  const { control } = useFormContext<BaselineIncomeAndExpensesFormShape>();

  return (
    <FormAwarePercentInput<BaselineIncomeAndExpensesFormShape>
      control={control}
      label={'Growth rate'}
      fieldName={
        'growthPercentage' as const satisfies BaselineIncomeAndExpensesField
      }
      isDecimalJSInput
      contextualHelp={<GiftGrowthRate />}
      required
    />
  );
}

export const BaselineIncomeAndExpensesFormFields = {
  Name,
  CashFlowType,
  Amount,
  AnnuallyRecurring,
  StartYear,
  EndYear,
  GrowthRate,
};
